/**
 * Reusable sortable data table with column definitions.
 *
 * Provides consistent table styling across all list views (findings,
 * scans, assets, reports, users). Supports custom cell renderers.
 */

import { useState, type ReactNode } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

/** Column definition for the data table. */
export interface Column<T> {
  /** Unique key matching a property on T (or a custom key for rendered columns). */
  key: string;
  /** Column header label or element (e.g. a select-all checkbox). */
  header: ReactNode;
  /** Whether this column is sortable. Defaults to false. */
  sortable?: boolean;
  /** Custom cell renderer. Receives the full row item. */
  render?: (item: T) => ReactNode;
  /** Tailwind classes for the column (e.g., width). */
  className?: string;
}

interface DataTableProps<T> {
  /** Column definitions. */
  columns: Column<T>[];
  /** Row data. */
  data: T[];
  /** Function to extract a unique key for each row. */
  rowKey: (item: T) => string;
  /** Optional callback when a row is clicked. */
  onRowClick?: (item: T) => void;
  /** Message shown when data is empty. */
  emptyMessage?: string;
}

type SortDirection = 'asc' | 'desc';

export default function DataTable<T>({
  columns,
  data,
  rowKey,
  onRowClick,
  emptyMessage = 'No data found.',
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  /** Toggle sort on a column. */
  function handleSort(key: string) {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  /** Sort data if a sort column is active. */
  const sortedData = sortKey
    ? [...data].sort((a, b) => {
        const aVal = (a as Record<string, any>)[sortKey];
        const bVal = (b as Record<string, any>)[sortKey];
        if (aVal === bVal) return 0;
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        const cmp = aVal < bVal ? -1 : 1;
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : data;

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 ${
                  col.sortable ? 'cursor-pointer select-none hover:text-gray-700' : ''
                } ${col.className ?? ''}`}
                onClick={col.sortable ? () => handleSort(col.key) : undefined}
              >
                <span className="inline-flex items-center gap-1">
                  {col.header}
                  {col.sortable && sortKey === col.key && (
                    sortDir === 'asc'
                      ? <ChevronUp className="h-3 w-3" />
                      : <ChevronDown className="h-3 w-3" />
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {sortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedData.map((item) => (
              <tr
                key={rowKey(item)}
                className={onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
              >
                {columns.map((col) => (
                  <td key={col.key} className={`px-4 py-3 text-sm text-gray-700 ${col.className ?? ''}`}>
                    {col.render ? col.render(item) : ((item as Record<string, any>)[col.key] as ReactNode) ?? '—'}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
